import { DOCUMENT } from '@angular/common';
import { Injectable, LOCALE_ID, WritableSignal, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, Event as RouterEvent } from '@angular/router';

import { filter } from 'rxjs/operators';

import { SITE_ORIGIN } from '../tokens/site-origin.token';
import { IPageBreadcrumb, PageHeaderService } from './page-header.service';

interface IJsonLdContext {
    readonly path: string;
    readonly data: IRouteSeoData;
    readonly canonicalUrl: string;
}

export interface IRouteSeoData {
    seoTitle?: string;
    seoDescription?: string;
    seoNoindex?: boolean;
}

const JSON_LD_MARKER_ATTR: string = 'data-sc-schema';

@Injectable({ providedIn: 'root' })
export class SeoService {
    readonly #router: Router = inject(Router);
    readonly #route: ActivatedRoute = inject(ActivatedRoute);
    readonly #title: Title = inject(Title);
    readonly #meta: Meta = inject(Meta);
    readonly #doc: Document = inject(DOCUMENT);
    readonly #siteOrigin: string = inject(SITE_ORIGIN);
    readonly #localeId: string = inject(LOCALE_ID);
    readonly #pageHeader: PageHeaderService = inject(PageHeaderService);

    // Latest navigation's SEO context; combined reactively with the breadcrumb
    // trail so dynamic crumbs (e.g. an article title set after navigation) flow
    // into the structured data too.
    readonly #jsonLdContext: WritableSignal<IJsonLdContext | null> = signal<IJsonLdContext | null>(null);

    constructor() {
        this.#router.events
            .pipe(
                filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe((event: NavigationEnd): void => {
                this.#apply(event.urlAfterRedirects);
            });

        effect((): void => {
            this.#renderJsonLd();
        });
    }

    #apply(url: string): void {
        const data: IRouteSeoData = this.#collectRouteData();
        const path: string = url.split('?')[0];
        const canonicalUrl: string = `${this.#siteOrigin}${path === '/' ? '/' : path}`;

        if (data.seoTitle) {
            this.#title.setTitle(data.seoTitle);
            this.#meta.updateTag({ property: 'og:title', content: data.seoTitle });
            this.#meta.updateTag({ name: 'twitter:title', content: data.seoTitle });
        }
        if (data.seoDescription) {
            this.#meta.updateTag({ name: 'description', content: data.seoDescription });
            this.#meta.updateTag({ property: 'og:description', content: data.seoDescription });
            this.#meta.updateTag({ name: 'twitter:description', content: data.seoDescription });
        }

        this.#setCanonical(canonicalUrl);
        this.#meta.updateTag({ property: 'og:url', content: canonicalUrl });
        this.#updateHreflangs(path);
        this.#updateRobots(data.seoNoindex === true);
        this.#jsonLdContext.set({ path, data, canonicalUrl });
    }

    /**
     * Toggles <meta name="robots"> between "index, follow" (default for public
     * pages, set in index.html) and "noindex, nofollow" for private routes
     * like /order/* checkout. Always rewrites the existing tag — never removes
     * it — so navigating from a private to a public route restores indexing.
     */
    #updateRobots(noindex: boolean): void {
        const content: string = noindex ? 'noindex, nofollow' : 'index, follow';
        this.#meta.updateTag({ name: 'robots', content });
    }

    #collectRouteData(): IRouteSeoData {
        let snap: ActivatedRouteSnapshot | null = this.#route.snapshot;
        let data: IRouteSeoData = {};
        while (snap !== null) {
            data = { ...data, ...(snap.data as IRouteSeoData) };
            snap = snap.firstChild;
        }
        return data;
    }

    #setCanonical(url: string): void {
        let link: HTMLLinkElement | null = this.#doc.querySelector('link[rel="canonical"]');
        if (!link) {
            link = this.#doc.createElement('link');
            link.setAttribute('rel', 'canonical');
            this.#doc.head.appendChild(link);
        }
        link.setAttribute('href', url);
    }

    /**
     * Rewrites every <link rel="alternate" hreflang="X" href="..."> so the
     * path segment matches the current route. The origin part of each href
     * stays the same (RU/COM/BY domains are hardcoded in index.html).
     * Without this pass, hreflang alternates always point to "/" on other
     * locales — Google treats that as broken language mapping.
     */
    #updateHreflangs(path: string): void {
        const links: NodeListOf<HTMLLinkElement> = this.#doc.querySelectorAll('link[rel="alternate"][hreflang]');
        const suffix: string = path === '/' ? '/' : path;
        links.forEach((link: HTMLLinkElement): void => {
            const current: string | null = link.getAttribute('href');
            if (!current) {
                return;
            }
            const origin: string | null = this.#extractOrigin(current);
            if (origin === null) {
                return;
            }
            link.setAttribute('href', `${origin}${suffix}`);
        });
    }

    #extractOrigin(href: string): string | null {
        const match: RegExpExecArray | null = /^(https?:\/\/[^/]+)/.exec(href);
        return match ? match[1] : null;
    }

    /**
     * Emits WebPage + BreadcrumbList JSON-LD for inner routes. The homepage
     * keeps the static WebApplication schema from index.html untouched — we
     * skip the root route intentionally.
     *
     * The managed <script> carries data-sc-schema so multiple navigations
     * update the same tag instead of leaving stale schemas behind.
     */
    /**
     * Emits WebPage + BreadcrumbList JSON-LD for inner routes. Reactive: reads
     * the breadcrumb trail signal so dynamic crumbs update the structured data,
     * and the BreadcrumbList mirrors the visible breadcrumbs (clean labels, full
     * nesting). Routes that declare no breadcrumb (e.g. the constructor) fall
     * back to a Home → page list built from the SEO title.
     */
    #renderJsonLd(): void {
        const ctx: IJsonLdContext | null = this.#jsonLdContext();
        const crumbs: readonly IPageBreadcrumb[] = this.#pageHeader.breadcrumbs();
        if (ctx === null || ctx.path === '/' || !ctx.data.seoTitle) {
            this.#removeManagedJsonLd();
            return;
        }

        const homeName: string = $localize`:@@navHome:Главная`;
        const items: Record<string, unknown>[] =
            crumbs.length > 1
                ? crumbs.map((crumb: IPageBreadcrumb, index: number): Record<string, unknown> => ({
                      '@type': 'ListItem',
                      position: index + 1,
                      name: crumb.label,
                      item: crumb.link ? `${this.#siteOrigin}${crumb.link}` : ctx.canonicalUrl,
                  }))
                : [
                      { '@type': 'ListItem', position: 1, name: homeName, item: `${this.#siteOrigin}/` },
                      { '@type': 'ListItem', position: 2, name: ctx.data.seoTitle, item: ctx.canonicalUrl },
                  ];

        const schema: Record<string, unknown> = {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'WebPage',
                    name: ctx.data.seoTitle,
                    description: ctx.data.seoDescription ?? '',
                    url: ctx.canonicalUrl,
                    inLanguage: this.#localeId,
                    isPartOf: { '@type': 'WebSite', url: `${this.#siteOrigin}/` },
                },
                {
                    '@type': 'BreadcrumbList',
                    itemListElement: items,
                },
            ],
        };

        this.#setManagedJsonLd(JSON.stringify(schema));
    }

    #setManagedJsonLd(jsonText: string): void {
        let script: HTMLScriptElement | null = this.#doc.querySelector(`script[${JSON_LD_MARKER_ATTR}]`);
        if (!script) {
            script = this.#doc.createElement('script');
            script.setAttribute('type', 'application/ld+json');
            script.setAttribute(JSON_LD_MARKER_ATTR, '');
            this.#doc.head.appendChild(script);
        }
        script.textContent = jsonText;
    }

    #removeManagedJsonLd(): void {
        const script: HTMLScriptElement | null = this.#doc.querySelector(`script[${JSON_LD_MARKER_ATTR}]`);
        script?.remove();
    }
}
